import {
  createFileRoute,
  Outlet,
  redirect,
  useLocation,
  useNavigate,
} from '@tanstack/react-router';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import type { AppAbility } from '@shared/types/ability';
import { SectionTabs, Stack } from '@shared/ui';
import { useAbility } from '@features/authorization';
import { useAuth } from '@features/auth';
import { Header } from '@widgets/header';

type SyncRouteContext = {
  auth: { isLoading: boolean; isAuthenticated: boolean };
  ability: AppAbility;
};

const canAccessSync = (ability: AppAbility) =>
  ability.can('manage', 'all') || ability.can('read', 'Dashboard');

/**
 * Sync Section Layout
 *
 * Route: /:locale/sync/*
 * Wraps all sync pages with dashboard layout
 *
 * This file is automatically code-split by TanStack Router.
 */
export const Route = createFileRoute('/$locale/_authenticated/sync')({
  beforeLoad: ({ context, params }) => {
    const { auth, ability } = context as unknown as SyncRouteContext;

    if (auth.isLoading || !auth.isAuthenticated) {
      return;
    }

    if (!canAccessSync(ability)) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({
        to: '/$locale/unauthorized',
        params: { locale: params.locale },
      });
    }
  },
  component: SyncLayout,
});

function SyncLayout() {
  const { locale: rawLocale } = Route.useParams();
  const locale = rawLocale as unknown as string;
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const ability = useAbility();
  const auth = useAuth();

  const localePrefix = `/${locale}`;
  const navItems = [
    { id: `${localePrefix}/sync/data-types`, label: t('sync.tabs.dataTypes', 'Data Types') },
    { id: `${localePrefix}/sync/option-sets`, label: t('sync.tabs.optionSets', 'Options') },
    { id: `${localePrefix}/sync/jobs`, label: t('sync.tabs.jobs', 'Jobs') },
    {
      id: `${localePrefix}/sync/relation-queue`,
      label: t('sync.tabs.relationQueue', 'Relation Queue'),
    },
    {
      id: `${localePrefix}/sync/sweep-runs`,
      label: t('sync.tabs.sweepRuns', 'Sweep Runs'),
    },
  ];

  const activeItemId =
    navItems.find((item) => location.pathname === item.id || location.pathname === `${item.id}/`)
      ?.id ??
    navItems.find((item) => location.pathname.startsWith(`${item.id}/`))?.id ??
    navItems[0]?.id;

  useEffect(() => {
    if (auth.isLoading || !auth.isAuthenticated) {
      return;
    }

    if (!canAccessSync(ability)) {
      void navigate({
        to: '/$locale/unauthorized',
        params: { locale },
        replace: true,
      });
    }
  }, [ability, auth.isAuthenticated, auth.isLoading, locale, navigate]);

  return (
    <div className="flex min-h-full flex-col pt-16 min-[992px]:pt-0">
      <Header locale={locale} className="min-[992px]:hidden" />

      <main className="min-w-0 p-6">
        <Stack gap="lg">
          <div className="mb-6">
            <SectionTabs
              items={navItems}
              activeId={activeItemId}
              mobileWrap
              onSelect={(id) => {
                if (!id) {
                  return;
                }
                if (id.endsWith('/sync/data-types')) {
                  void navigate({ to: '/$locale/sync/data-types', params: { locale } });
                  return;
                }

                if (id.endsWith('/sync/option-sets')) {
                  void navigate({ to: '/$locale/sync/option-sets', params: { locale } });
                  return;
                }

                if (id.endsWith('/sync/jobs')) {
                  void navigate({ to: '/$locale/sync/jobs', params: { locale } });
                  return;
                }

                if (id.endsWith('/sync/relation-queue')) {
                  void navigate({ to: '/$locale/sync/relation-queue', params: { locale } });
                  return;
                }

                if (id.endsWith('/sync/sweep-runs')) {
                  void navigate({ to: '/$locale/sync/sweep-runs', params: { locale } });
                  return;
                }

                void navigate({ to: '/$locale/sync/data-types', params: { locale } });
              }}
            />
          </div>

          <Outlet />
        </Stack>
      </main>
    </div>
  );
}
